import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { organization, fetchMe } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    org_name: organization?.name || '', sector: '', country: 'Svizzera',
    project_name: '', market: '', primary_language: 'it',
  });

  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === 'string' ? v : v.target.value });

  const handleFinish = async () => {
    setLoading(true);
    try {
      await api.post('/projects', {
        name: form.project_name, sector: form.sector, market: form.market,
        primary_language: form.primary_language,
      });
      await fetchMe();
      toast.success('Progetto creato con successo!');
      navigate('/foundation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione del progetto');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Organizzazione' },
    { num: 2, title: 'Progetto' },
    { num: 3, title: 'Conferma' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-8" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Building2 className="w-10 h-10 text-[#1A3A5C] mx-auto mb-3" />
          <h1 className="text-3xl font-bold text-[#1A3A5C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Configurazione iniziale</h1>
          <p className="text-[#64748B] mt-1">Configura il tuo spazio di lavoro in 3 step</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= s.num ? 'bg-[#1A3A5C] text-white' : 'bg-[#E2E8F0] text-[#64748B]'}`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              {i < steps.length - 1 && <div className={`w-12 h-0.5 mx-2 ${step > s.num ? 'bg-[#1A3A5C]' : 'bg-[#E2E8F0]'}`} />}
            </div>
          ))}
        </div>

        <Card className="shadow-lg border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="text-xl text-[#1A3A5C]">{steps[step - 1].title}</CardTitle>
            <CardDescription>
              {step === 1 && 'Inserisci i dettagli della tua organizzazione'}
              {step === 2 && 'Configura il tuo primo progetto'}
              {step === 3 && 'Rivedi e conferma'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Nome organizzazione</Label>
                  <Input value={form.org_name} onChange={set('org_name')} placeholder="HumanAImpact" data-testid="onboarding-org-name" />
                </div>
                <div className="space-y-2">
                  <Label>Settore</Label>
                  <Input value={form.sector} onChange={set('sector')} placeholder="Coaching & Formazione AI" data-testid="onboarding-sector" />
                </div>
                <div className="space-y-2">
                  <Label>Paese</Label>
                  <Input value={form.country} onChange={set('country')} placeholder="Svizzera" data-testid="onboarding-country" />
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label>Nome progetto</Label>
                  <Input value={form.project_name} onChange={set('project_name')} placeholder="HumanAImpact Marketing" data-testid="onboarding-project-name" />
                </div>
                <div className="space-y-2">
                  <Label>Mercato</Label>
                  <Input value={form.market} onChange={set('market')} placeholder="Svizzera, DACH" data-testid="onboarding-market" />
                </div>
                <div className="space-y-2">
                  <Label>Lingua principale</Label>
                  <Select value={form.primary_language} onValueChange={set('primary_language')}>
                    <SelectTrigger data-testid="onboarding-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {step === 3 && (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-[#E2E8F0]">
                  <span className="text-[#64748B]">Organizzazione</span>
                  <span className="font-medium text-[#1A3A5C]">{form.org_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#E2E8F0]">
                  <span className="text-[#64748B]">Settore</span>
                  <span className="font-medium text-[#1A3A5C]">{form.sector}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#E2E8F0]">
                  <span className="text-[#64748B]">Progetto</span>
                  <span className="font-medium text-[#1A3A5C]">{form.project_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#E2E8F0]">
                  <span className="text-[#64748B]">Mercato</span>
                  <span className="font-medium text-[#1A3A5C]">{form.market}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-[#64748B]">Lingua</span>
                  <span className="font-medium text-[#1A3A5C]">{{ it: 'Italiano', en: 'English', de: 'Deutsch', fr: 'Français' }[form.primary_language]}</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1" data-testid="onboarding-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
                </Button>
              )}
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} className="flex-1 bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid="onboarding-next-btn">
                  Avanti <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleFinish} className="flex-1 bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" disabled={loading} data-testid="onboarding-finish-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Crea Progetto
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
